import React, { useEffect } from 'react';
import { generateMockJobData } from '../data/mockData';

interface JobDetailProps {
  jobId: string;
  darkMode?: boolean;
  onClose?: () => void;
}

const JobDetail: React.FC<JobDetailProps> = ({ jobId, darkMode = false, onClose }) => {
  useEffect(() => {
    // Find the job from mock data
    const mockData = generateMockJobData();
    const features = mockData.features;
    const foundFeature = features.find(f => f.properties.id === jobId);
    
    if (foundFeature) {
      // Use the existing showJobDetailsPopup function from App.tsx
      const jobData = {
        id: foundFeature.properties.id,
        title: foundFeature.properties.title,
        company: foundFeature.properties.company,
        location: foundFeature.properties.city,
        city: foundFeature.properties.city,
        country: foundFeature.properties.countryCode || 'USA',
        salary: foundFeature.properties.salary,
        employmentType: foundFeature.properties.employmentType,
        remote: foundFeature.properties.remote || false,
        coordinates: foundFeature.geometry.coordinates,
        industry: foundFeature.properties.industryTag || 'Technology',
        companyRating: foundFeature.properties.companyRating,
        benefits: foundFeature.properties.benefits,
        yearsOfExperience: foundFeature.properties.yearsOfExperience,
        description: foundFeature.properties.description
      };
      
      // Create the popup manually using the same HTML structure as in App.tsx
        const fullscreenDiv = document.createElement('div');
        fullscreenDiv.className = 'job-fullscreen-overlay';
        fullscreenDiv.innerHTML = `
          <div class="job-fullscreen-popup ${darkMode ? 'dark' : ''}">
            <div class="fullscreen-overlay">
              <div class="fullscreen-content">
                <button class="fullscreen-close-btn" onclick="this.closest('.job-fullscreen-overlay').remove()">×</button>
                
                <div class="fullscreen-header">
                  <div class="job-main-info">
                    <h1>${jobData.title}</h1>
                    <div class="company-info-large">
                      <span class="company-name-large">${jobData.company}</span>
                      <div class="company-rating-badge">
                        <span class="star">★</span>
                        <span>${jobData.companyRating || '4.2'}</span>
                      </div>
                    </div>
                    <div class="location-large">${jobData.location}</div>
                    <p class="job-description-large">${jobData.title} position at ${jobData.company}. Looking for talented professionals to join our ${jobData.industry} team.</p>
                  </div>
                  <div class="salary-showcase">
                    <div class="salary-amount-large">$${jobData.salary.toLocaleString()}</div>
                    <div class="salary-label-large">Annual Salary</div>
                    <div class="salary-percentile-large">
                      <div class="percentile-bar-large">
                        <div class="percentile-fill-large" style="width: 52.63%"></div>
                        <div class="percentile-marker-large" style="left: 52.63%"></div>
                      </div>
                      <div class="percentile-label-large">Top 47% in ${jobData.location}</div>
                    </div>
                  </div>
                </div>
                
                <div class="fullscreen-body">
                  <div class="left-column">
                    <div class="job-details-section">
                      <h3>Job Details</h3>
                      <div class="job-meta-large">
                        <div class="meta-item-large">${jobData.employmentType}</div>
                        ${jobData.remote ? '<div class="meta-item-large remote">Remote</div>' : ''}
                        <div class="meta-item-large">${jobData.industry}</div>
                        <div class="meta-item-large">Mid-Senior level</div>
                      </div>
                    </div>
                    
                    <div class="company-metrics-section">
                      <h3>Company Ratings</h3>
                      <div class="metrics-grid-large">
                        <div class="metric-card-large">
                          <div class="metric-header-large">
                            <span class="metric-title-large">Overall Rating</span>
                            <span class="metric-score-large">4.2/5</span>
                          </div>
                          <div class="metric-bar-large">
                            <div class="fill-large" style="width: 84%"></div>
                          </div>
                        </div>
                        <div class="metric-card-large">
                          <div class="metric-header-large">
                            <span class="metric-title-large">Work-Life Balance</span>
                            <span class="metric-score-large">4.1/5</span>
                          </div>
                          <div class="metric-bar-large">
                            <div class="fill-large" style="width: 82%"></div>
                          </div>
                        </div>
                        <div class="metric-card-large">
                          <div class="metric-header-large">
                            <span class="metric-title-large">Compensation</span>
                            <span class="metric-score-large">4.3/5</span>
                          </div>
                          <div class="metric-bar-large">
                            <div class="fill-large" style="width: 86%"></div>
                          </div>
                        </div>
                        <div class="metric-card-large">
                          <div class="metric-header-large">
                            <span class="metric-title-large">Career Growth</span>
                            <span class="metric-score-large">3.9/5</span>
                          </div>
                          <div class="metric-bar-large">
                            <div class="fill-large" style="width: 78%"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="right-column">
                    <div class="salary-comparison-large">
                      <h3>Salary Comparison</h3>
                      <div class="comparison-section">
                        <h4>Regional Comparison - ${jobData.location}</h4>
                        <div class="comparison-grid-large">
                          <div class="comparison-item-large">
                            <span class="comparison-label-large">This Position</span>
                            <span class="comparison-value-large highlight">$${jobData.salary.toLocaleString()}</span>
                          </div>
                          <div class="comparison-item-large">
                            <span class="comparison-label-large">Average ${jobData.title}</span>
                            <span class="comparison-value-large">$${Math.round(jobData.salary * 0.95).toLocaleString()}</span>
                          </div>
                          <div class="comparison-item-large">
                            <span class="comparison-label-large">Entry Level</span>
                            <span class="comparison-value-large">$${Math.round(jobData.salary * 0.665).toLocaleString()}</span>
                          </div>
                          <div class="comparison-item-large">
                            <span class="comparison-label-large">Senior Level</span>
                            <span class="comparison-value-large">$${Math.round(jobData.salary * 1.33).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div class="benefits-section-large">
                      <h3>Benefits & Perks</h3>
                      <div class="benefits-grid-large">
                        <div class="benefit-item-large included">✓ Health insurance</div>
                        <div class="benefit-item-large included">✓ Paid time off</div>
                        <div class="benefit-item-large included">✓ Remote work</div>
                        <div class="benefit-item-large not-included">✗ Stock options</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Income Breakdown Section -->
                <div class="sankey-section">
                  <h3>Income Breakdown</h3>
                  <div class="income-breakdown-compact">
                    <div class="income-flow-section">
                      <h4>Annual Income Flow</h4>
                      <div class="income-flow-grid">
                        <div class="income-flow-item">
                          <span class="income-icon material-icons">account_balance_wallet</span>
                          <span class="income-label">Gross Salary</span>
                          <span class="income-value">$${jobData.salary.toLocaleString()}</span>
                          <div class="income-bar">
                            <div class="income-fill" style="width: 100%; background-color: rgb(31, 119, 180);"></div>
                          </div>
                        </div>
                        <div class="income-flow-item deduction">
                          <span class="income-icon material-icons">receipt_long</span>
                          <span class="income-label">Income Tax</span>
                          <span class="income-value">-$${Math.round(jobData.salary * 0.22).toLocaleString()}</span>
                          <div class="income-bar">
                            <div class="income-fill" style="width: 22%; background-color: rgb(255, 127, 14);"></div>
                          </div>
                        </div>
                        <div class="income-flow-item deduction">
                          <span class="income-icon material-icons">security</span>
                          <span class="income-label">Social Security</span>
                          <span class="income-value">-$${Math.round(jobData.salary * 0.08).toLocaleString()}</span>
                          <div class="income-bar">
                            <div class="income-fill" style="width: 8%; background-color: rgb(44, 160, 44);"></div>
                          </div>
                        </div>
                        <div class="income-flow-item net">
                          <span class="income-icon material-icons">savings</span>
                          <span class="income-label">Net Income</span>
                          <span class="income-value">$${Math.round(jobData.salary * 0.7).toLocaleString()}</span>
                          <div class="income-bar">
                            <div class="income-fill" style="width: 70%; background-color: rgb(214, 39, 40);"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="monthly-breakdown-section">
                      <h4>Monthly Expense Distribution</h4>
                      <svg class="expense-breakdown-chart" viewBox="0 0 600 200" preserveAspectRatio="xMidYMid meet">
                        <rect x="0" y="0" rx="4" ry="4" width="140" height="25" fill="#9467bd" fill-opacity="1" stroke-width="0">
                          <title>Rent: $${Math.round(jobData.salary * 0.7 * 0.35 / 12).toLocaleString()} (35%)</title>
                        </rect>
                        <text x="145" y="17" fill="var(--text-primary, #1a1a1a)" font-size="11" font-weight="500">Rent: $${Math.round(jobData.salary * 0.7 * 0.35 / 12).toLocaleString()} (35%)</text>
                        <rect x="0" y="30" rx="4" ry="4" width="60" height="25" fill="#8c564b" fill-opacity="1" stroke-width="0">
                          <title>Food: $${Math.round(jobData.salary * 0.7 * 0.15 / 12).toLocaleString()} (15%)</title>
                        </rect>
                        <text x="65" y="47" fill="var(--text-primary, #1a1a1a)" font-size="11" font-weight="500">Food: $${Math.round(jobData.salary * 0.7 * 0.15 / 12).toLocaleString()} (15%)</text>
                        <rect x="0" y="60" rx="4" ry="4" width="40" height="25" fill="#e377c2" fill-opacity="1" stroke-width="0">
                          <title>Transport: $${Math.round(jobData.salary * 0.7 * 0.10 / 12).toLocaleString()} (10%)</title>
                        </rect>
                        <text x="45" y="77" fill="var(--text-primary, #1a1a1a)" font-size="11" font-weight="500">Transport: $${Math.round(jobData.salary * 0.7 * 0.10 / 12).toLocaleString()} (10%)</text>
                        <rect x="0" y="90" rx="4" ry="4" width="32" height="25" fill="#7f7f7f" fill-opacity="1" stroke-width="0">
                          <title>Utilities: $${Math.round(jobData.salary * 0.7 * 0.08 / 12).toLocaleString()} (8%)</title>
                        </rect>
                        <text x="37" y="107" fill="var(--text-primary, #1a1a1a)" font-size="11" font-weight="500">Utilities: $${Math.round(jobData.salary * 0.7 * 0.08 / 12).toLocaleString()} (8%)</text>
                        <rect x="0" y="120" rx="4" ry="4" width="40" height="25" fill="#bcbd22" fill-opacity="1" stroke-width="0">
                          <title>Entertainment: $${Math.round(jobData.salary * 0.7 * 0.10 / 12).toLocaleString()} (10%)</title>
                        </rect>
                        <text x="45" y="137" fill="var(--text-primary, #1a1a1a)" font-size="11" font-weight="500">Entertainment: $${Math.round(jobData.salary * 0.7 * 0.10 / 12).toLocaleString()} (10%)</text>
                        <rect x="0" y="150" rx="4" ry="4" width="88" height="25" fill="#17becf" fill-opacity="1" stroke-width="0">
                          <title>Savings: $${Math.round(jobData.salary * 0.7 * 0.22 / 12).toLocaleString()} (22%)</title>
                        </rect>
                        <text x="93" y="167" fill="var(--text-primary, #1a1a1a)" font-size="11" font-weight="500">Savings: $${Math.round(jobData.salary * 0.7 * 0.22 / 12).toLocaleString()} (22%)</text>
                      </svg>
                    </div>
                    <div class="income-summary-row">
                      <div class="summary-stat">
                        <span class="stat-label">Monthly Net</span>
                        <span class="stat-value">$${Math.round(jobData.salary * 0.7 / 12).toLocaleString()}</span>
                      </div>
                      <div class="summary-stat">
                        <span class="stat-label">Tax Rate</span>
                        <span class="stat-value">30%</span>
                      </div>
                      <div class="summary-stat">
                        <span class="stat-label">Savings Rate</span>
                        <span class="stat-value">22%</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="fullscreen-footer">
                  <div class="action-buttons-large">
                    <button class="apply-btn-fullscreen">Apply for this Position</button>
                    <button class="save-job-btn-fullscreen">Save Job</button>
                    <button class="share-job-btn-fullscreen">Share Job</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `;
        
        document.body.appendChild(fullscreenDiv);
      
      // Clean up - call onClose when popup is closed
      if (onClose) {
        const checkInterval = setInterval(() => {
          if (!document.querySelector('.job-fullscreen-overlay')) {
            clearInterval(checkInterval);
            onClose();
          }
        }, 100);
      }
    }
  }, [jobId, darkMode, onClose]);

  // This component doesn't render anything - it just triggers the popup
  return null;
};

export default JobDetail;